import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Drawer,
  Divider,
  CircularProgress,
  InputAdornment,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import apiClient, { getApiErrorInfo } from '../../services/api';
import type { Course } from '../../services/courseService';
import { CourseType } from '../../services/courseService';
import { getFieldsOfStudy } from '../../utils/fieldOfStudy';

const COURSE_TYPE_LABELS: Record<number, string> = {
  [CourseType.OnDemand]: 'On-Demand',
  [CourseType.Webinar]: 'Webinar',
};

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  completed: 'success',
  in_progress: 'warning',
  cancelled: 'error',
};

interface CourseStats {
  courseId: number;
  totalRegistrations: number;
  completedRegistrations: number;
}

interface CourseRegistration {
  id: string;
  userId: string;
  userFirstName: string;
  userLastName: string;
  userEmail: string;
  tenantName: string;
  status: string;
  registeredAt: string;
  completedAt?: string;
  certificateUrl?: string;
}

export default function GlobalCourses() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [statsMap, setStatsMap] = useState<Map<number, CourseStats>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('');

  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [detailRegistrations, setDetailRegistrations] = useState<CourseRegistration[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    loadCourses();
  }, []);

  async function loadCourses() {
    setLoading(true);
    setError(null);
    try {
      // Use admin/all to get every course without tenant filtering
      const coursesRes = await apiClient.get('/api/courses/admin/all');
      setCourses(coursesRes.data);
    } catch (err) {
      setError(getApiErrorInfo(err).message || 'Failed to load courses.');
    } finally {
      setLoading(false);
    }

    // Load stats independently — a stats failure must not prevent the course list from showing
    try {
      const statsRes = await apiClient.get('/api/courses/admin/stats');
      const map = new Map<number, CourseStats>();
      for (const s of statsRes.data as CourseStats[]) {
        map.set(s.courseId, s);
      }
      setStatsMap(map);
    } catch {
      // Stats are non-critical; silently ignore failures
    }
  }

  async function openCourseDetail(course: Course) {
    setSelectedCourse(course);
    setDetailLoading(true);
    setDetailRegistrations([]);
    try {
      const res = await apiClient.get(`/api/courses/${course.id}/registrations`);
      setDetailRegistrations(res.data);
    } catch {
      // Keep empty list on error; don't block the detail pane
    } finally {
      setDetailLoading(false);
    }
  }

  function closeCourseDetail() {
    setSelectedCourse(null);
    setDetailRegistrations([]);
  }

  const filtered = courses.filter((c) => {
    const matchesSearch =
      !searchTerm ||
      c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getFieldsOfStudy(c).some((f) => f.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = filterType === '' || String(c.courseType) === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search by title or field of study…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ flex: '1 1 280px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Course Type</InputLabel>
          <Select
            value={filterType}
            label="Course Type"
            onChange={(e) => setFilterType(e.target.value)}
            startAdornment={<FilterIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />}
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value={String(CourseType.OnDemand)}>On-Demand</MenuItem>
            <MenuItem value={String(CourseType.Webinar)}>Webinar</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Field of Study</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Credits</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Registrations</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8, color: 'text.secondary' }}>
                    No courses found.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((course) => {
                  const stats = statsMap.get(course.id);
                  return (
                    <TableRow
                      key={course.id}
                      hover
                      onClick={() => openCourseDetail(course)}
                      sx={{ cursor: 'pointer' }}
                      selected={selectedCourse?.id === course.id}
                    >
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {course.title}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{getFieldsOfStudy(course).join(', ') || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={COURSE_TYPE_LABELS[course.courseType] ?? '—'}
                          size="small"
                          color={course.courseType === CourseType.Webinar ? 'secondary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{course.creditHours ?? '—'}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{course.level ?? '—'}</TableCell>
                      <TableCell>
                        {stats ? (
                          <Tooltip title={`${stats.completedRegistrations} completed`}>
                            <Typography variant="body2">{stats.totalRegistrations}</Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'text.disabled' }}>0</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={course.isActive === false ? 'Inactive' : 'Active'}
                          size="small"
                          color={course.isActive === false ? 'error' : 'success'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Course detail drawer */}
      <Drawer
        anchor="right"
        open={!!selectedCourse}
        onClose={closeCourseDetail}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, display: 'flex', flexDirection: 'column' } }}
      >
        {selectedCourse && (
          <>
            {/* Drawer header */}
            <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
              <Typography variant="h6" sx={{ flexGrow: 1, mr: 1 }} noWrap title={selectedCourse.title}>
                {selectedCourse.title}
              </Typography>
              <IconButton size="small" onClick={closeCourseDetail}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Course metadata */}
            <Box sx={{ px: 3, py: 2, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip
                  label={COURSE_TYPE_LABELS[selectedCourse.courseType] ?? '—'}
                  size="small"
                  color={selectedCourse.courseType === CourseType.Webinar ? 'secondary' : 'default'}
                  variant="outlined"
                />
                {selectedCourse.creditHours != null && (
                  <Chip label={`${selectedCourse.creditHours} Credits`} size="small" variant="outlined" />
                )}
                {selectedCourse.level && (
                  <Chip label={selectedCourse.level} size="small" variant="outlined" />
                )}
                <Chip
                  label={selectedCourse.isActive === false ? 'Inactive' : 'Active'}
                  size="small"
                  color={selectedCourse.isActive === false ? 'error' : 'success'}
                  variant="outlined"
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: selectedCourse.description ? 1.5 : 0 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Field of Study</Typography>
                  <Typography variant="body2">{getFieldsOfStudy(selectedCourse).join(', ') || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Developer</Typography>
                  <Typography variant="body2">{selectedCourse.developer || '—'}</Typography>
                </Box>
              </Box>

              {selectedCourse.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {selectedCourse.description}
                </Typography>
              )}
            </Box>

            <Divider />

            {/* Registrations header */}
            <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 1.5, flexShrink: 0 }}>
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>Registrations</Typography>
              {!detailLoading && (
                <Chip label={detailRegistrations.length} size="small" />
              )}
            </Box>

            {/* Registrations list */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {detailLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : detailRegistrations.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 3, py: 2 }}>
                  No registrations found for this course.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Organization</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Registered</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailRegistrations.map((reg) => (
                        <TableRow key={reg.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {reg.userFirstName} {reg.userLastName}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', maxWidth: 180 }}>
                            <Typography variant="body2" noWrap title={reg.userEmail}>
                              {reg.userEmail}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{reg.tenantName}</TableCell>
                          <TableCell>
                            <Chip
                              label={reg.status}
                              size="small"
                              color={STATUS_COLORS[reg.status] ?? 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                            {new Date(reg.registeredAt).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </>
        )}
      </Drawer>

    </Box>
  );
}
